<template>
  <v-row
    v-if="paymentTerminal"
    justify="center"
  >
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="850"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          class="pl-1"
          flat
          height="80"
          max-height="80"
        >
          <v-avatar color="primary">
            <v-icon dark>
              mdi-credit-card-wireless
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Settings.Payment.EditTerminal.Toolbar.Title') }}
            </div>

            <div class="text-caption">
              {{ $t('Settings.Payment.EditTerminal.Toolbar.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-1">
          <v-form
            ref="paymentTerminalForm"
            v-model="paymentTerminalFormValid"
            @submit.prevent
          >
            <v-container
              class=""
              fluid
            >
              <v-row>
                <v-col class="grow">
                  <v-text-field
                    v-model="paymentTerminal.Nickname"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('Nickname')"
                    label="Nickname"
                    type="text"
                  />
                </v-col>

                <v-col class="shrink">
                  <div class="d-inline-block">
                    <p class="grey--text text-caption ma-0 text-no-wrap">
                      Terminal Active
                    </p>
                    <v-switch
                      v-model="paymentTerminal.Active"
                      :label="paymentTerminal.Active ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0"
                      color="green"
                      inset
                    />
                  </div>
                </v-col>

                <v-col
                  class="pa-0"
                  cols="12"
                />

                <template v-if="isReadonly">
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="paymentTerminal.ApiKey"
                      disabled
                      hide-details
                      label="Api Key"
                      type="text"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      :items="$t('Settings.Payment.Terminal.Vendor.Mellon.Acquirer')"
                      :value="`${getVendorAcquirerById(paymentTerminal.PosVendorId, paymentTerminal.AcquirerCode).Name} / ${getVendorAcquirerById(paymentTerminal.PosVendorId, paymentTerminal.AcquirerCode).SubAcquirer}`"
                      disabled
                      hide-details
                      label="Acquirer"
                    />
                  </v-col>
                </template>

                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="paymentTerminal.TerminalId"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('TerminalId')"
                    disabled
                    label="Terminal Id"
                    type="text"
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-if="paymentTerminal.PosVendorId === 1"
                    v-model="paymentTerminal.VirtualTerminalId"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('VirtualTerminalId')"
                    disabled
                    label="Virtual Terminal Id"
                    type="text"
                  />

                  <v-text-field
                    v-else-if="paymentTerminal.PosVendorId === 2"
                    v-model="paymentTerminal.TId"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('TId')"
                    disabled
                    label="TId"
                    type="text"
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="paymentTerminal.Manufactor"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('Manufactor')"
                    disabled
                    label="Manufacturer"
                    type="text"
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="paymentTerminal.Model"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('Model')"
                    disabled
                    label="Model"
                    type="text"
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="paymentTerminal.OperationSystem"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('OperationSystem')"
                    disabled
                    label="Operation System"
                    type="text"
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="paymentTerminal.Connection"
                    :rules="paymentTerminal.validator.vuetifyFormFieldRules('Connection')"
                    disabled
                    label="Connection"
                    type="text"
                  />
                </v-col>

                <v-col cols="12">
                  <div class="d-inline-block">
                    <p class="grey--text text-caption ma-0 text-no-wrap">
                      Instalments Active
                    </p>
                    <v-switch
                      v-model="paymentTerminal.HasInstalments"
                      :label="paymentTerminal.HasInstalments ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0"
                      color="blue"
                      inset
                    />
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
          <v-btn
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import TerminalsCommon from '@/mixins/payments/terminalsCommon'

export default {
  name      : 'TerminalEditDialog',
  components: {},
  mixins    : [AppData, Auth, TerminalsCommon],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },
    paymentTerminal: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      paymentTerminalFormValid: false
    }
  },
  computed: {
    isReadonly() {
      return this.paymentTerminal?.PosVendorId === 2
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(newVal) {
    }
  },
  created() {
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    onSaveButtonClick() {
      this.$refs.paymentTerminalForm.validate()

      if (this.paymentTerminalFormValid) {
        this.$emit('payment-terminal-save', this.paymentTerminal)
      }
    },

    onCancelButtonClick() {
      if (this.$refs.paymentTerminalForm) this.$refs.paymentTerminalForm.resetValidation()
      this.isVisible = false
    }
  }
}
</script>
